import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { merge, Observable, of, Subscription } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AttachmentSourceOptionService, ISourceOption } from './attachment-source-option.service';

@Component({
  selector: 'app-attachment-source-restriction',
  templateUrl: './attachment-source-restrictions.component.html',
  styleUrl: '../attachment-restrictions.component.scss',
  standalone: false,
})
export class AttachmentSourceRestrictionComponent implements OnInit, OnDestroy, OnChanges {
  @Input() templateEntity: string;
  @Input() fileSection: FormGroup;
  @Output() attachmentSourceInitialised = new EventEmitter<void>();

  sourceOptions$: Observable<ISourceOption[]>;
  alreadySelectedPaths: string[] = [];
  updateConfig$ = new Subscription();

  hasOnlyOneAvailableSourceEntity = false;

  constructor(private attachmentSourceOptionService: AttachmentSourceOptionService) {}

  get attachmentSourceEntitiesControl(): any {
    return this.fileSection.get('config').get('attachmentSourceEntities');
  }

  ngOnInit(): void {
    this.setupDefaultPath();
    this.sourceOptions$ = this.setupSourceOptions();
    this.checkAvailableSourceEntityCount();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateEntity'] && !changes['templateEntity'].isFirstChange()) {
      this.reset();
      this.setupDefaultPath();
      this.checkAvailableSourceEntityCount();
    }
  }

  ngOnDestroy(): void {
    this.updateConfig$.unsubscribe();
  }

  selected($event: ISourceOption) {
    const selectedPath = $event.path;
    const currentPaths: string[] = this.attachmentSourceEntitiesControl.value;
    if (!currentPaths.includes(selectedPath)) {
      currentPaths.push(selectedPath);
      this.attachmentSourceEntitiesControl.setValue(currentPaths);
    }
  }

  onRemove($event: ISourceOption) {
    const selectedPath = $event.path;
    const currentPaths: string[] = this.attachmentSourceEntitiesControl.value;
    if (currentPaths.includes(selectedPath)) {
      currentPaths.splice(currentPaths.indexOf(selectedPath), 1);
      this.attachmentSourceEntitiesControl.setValue(currentPaths);
    }
  }

  private setupSourceOptions() {
    const initialValue$: Observable<string[]> = of(this.attachmentSourceEntitiesControl.value);
    const onChange$: Observable<string[]> = this.attachmentSourceEntitiesControl.valueChanges;
    return merge(initialValue$, onChange$).pipe(
      tap((paths) => (this.alreadySelectedPaths = paths)),
      switchMap((paths) => this.attachmentSourceOptionService.getOptions(paths))
    );
  }

  private reset() {
    this.attachmentSourceEntitiesControl.setValue([]);
  }

  private setupDefaultPath() {
    this.setDefaultPath().pipe(
        tap((_) => this.attachmentSourceInitialised.emit()))
      .subscribe();
  }

  private setDefaultPath(): Observable<void> {
    const noTemplateEntitySelected = !this.templateEntity;
    const hasAlreadySelectedPaths = this.attachmentSourceEntitiesControl.value?.length > 0;
    if (noTemplateEntitySelected || hasAlreadySelectedPaths) {
      return of(void 0);
    }

    return this.attachmentSourceOptionService
      .getDefaultOption(this.templateEntity)
      .pipe(map((option: ISourceOption) => this.attachmentSourceEntitiesControl.setValue([option.path])));
  }

  private checkAvailableSourceEntityCount() {
    if (!this.templateEntity) {
      this.hasOnlyOneAvailableSourceEntity = false;
      return;
    }

    this.attachmentSourceOptionService.getAvailableEntitiesForSource(this.templateEntity).subscribe((options) => {
      this.hasOnlyOneAvailableSourceEntity = options.length === 1;
      this.updateValidation(options.length);
    });
  }

  private updateValidation(availableOptionsCount: number) {
    const validators = availableOptionsCount > 1 ? [this.checkAtLeastOneSourceEntityValidator] : null;
    this.attachmentSourceEntitiesControl.setValidators(validators);
    this.attachmentSourceEntitiesControl.updateValueAndValidity({emitEvent: false});
  }

  private checkAtLeastOneSourceEntityValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.length === 0) {
      return { requiredSourceEntity: true };
    }
    return null;
  }
}
